import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiTextBoxPlusOutline } from '@mdi/js'
import { useCan } from '../../contexts/PermissionsContext'
import { useAssetKnowledge } from '../../hooks/useKnowledge'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { Input } from '../ui/input'
import { KindBadge } from '../knowledge/KindBadge'
import { DetailSection } from './detail-section'

/**
 * The notes that apply to the thread's asset (spec 2026-11-01-01), filtered
 * client-side, each with *Insert*, which appends its text to the composer for
 * the manager to rewrite. No paid call: the same read as the asset page.
 */
export function ConversationKnowledgeSection({
    org,
    assetUid,
    onInsert,
}: {
    org: string
    assetUid?: string
    onInsert: (text: string) => void
}) {
    const { t } = useTranslation('knowledge')
    const canRead = useCan('knowledge.read')
    const { data: notes, isLoading } = useAssetKnowledge(org, canRead ? assetUid : undefined)
    const [q, setQ] = useState('')
    const shown = useMemo(() => {
        const needle = q.trim().toLowerCase()
        if (!needle) return notes ?? []

        return (notes ?? []).filter((n) => `${n.title ?? ''} ${n.text_content ?? ''}`.toLowerCase().includes(needle))
    }, [notes, q])

    if (!canRead || !assetUid) return null

    return (
        <DetailSection sectionKey="knowledge" title={t('conversation.section')} testId="conversation-knowledge">
            {isLoading ? (
                <p className="text-xs text-muted-foreground">{t('loading')}</p>
            ) : (notes ?? []).length === 0 ? (
                <p className="text-xs text-muted-foreground" data-testid="conversation-knowledge-empty">
                    {t('conversation.empty')}
                </p>
            ) : (
                <>
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder={t('conversation.search')}
                        aria-label={t('conversation.search')}
                        className="h-7 text-xs"
                        data-testid="conversation-knowledge-search"
                    />
                    {shown.length === 0 ? (
                        <p className="text-xs text-muted-foreground">{t('conversation.no_match')}</p>
                    ) : (
                        <ul className="space-y-1.5">
                            {shown.map((n) => (
                                <li key={n.uid} className="rounded-md border bg-card p-2 space-y-1" data-testid="conversation-knowledge-item">
                                    <div className="flex items-center justify-between gap-1">
                                        <div className="flex min-w-0 items-center gap-1.5">
                                            <KindBadge kind={n.kind} />
                                            <span className="truncate text-xs font-medium text-foreground">{n.title}</span>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 px-1.5 text-xs"
                                            disabled={!n.text_content}
                                            onClick={() => onInsert(n.text_content ?? '')}
                                            data-testid="conversation-knowledge-insert"
                                        >
                                            <AppIcon path={mdiTextBoxPlusOutline} size="xs" className="mr-1" />
                                            {t('conversation.insert')}
                                        </Button>
                                    </div>
                                    {n.text_content && <p className="line-clamp-3 whitespace-pre-wrap text-[11px] text-muted-foreground">{n.text_content}</p>}
                                    <div className="text-[10px] text-muted-foreground">
                                        {n.source.type === 'organization'
                                            ? t('source_organization')
                                            : t('source_asset', { name: n.source.asset_name ?? '' })}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}
        </DetailSection>
    )
}
